import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Users, Plus, Search, ArrowLeft, MapPin, 
  Phone, Mail, BarChart3, Package, Truck, Upload
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function CustomersPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    county: "",
    postcode: "",
    country: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await api.get("/customers");
      setCustomers(res.data);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address) {
      toast.error("Name and address are required");
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post("/customers", formData);
      toast.success("Customer added successfully");
      setDialogOpen(false);
      setFormData({ name: "", email: "", phone: "", address: "", city: "", county: "", postcode: "", country: "", notes: "" });
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add customer");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customers-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <BarChart3 className="h-5 w-5" />
            Dashboard
          </Link>
          <Link to="/admin/jobs" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Package className="h-5 w-5" />
            Delivery Jobs
          </Link>
          <Link to="/admin/customers" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Users className="h-5 w-5" />
            Customers
          </Link>
          <Link to="/admin/drivers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Truck className="h-5 w-5" />
            Drivers
          </Link>
          <Link to="/admin/upload" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Upload className="h-5 w-5" />
            Import Data
          </Link>
        </nav>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="font-semibold">Customers</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Customers</h1>
              <p className="text-slate-600">{customers.length} total customers</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/upload")}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="add-customer-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Customer
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-customers"
            />
          </div>

          {/* Customers Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCustomers.map((customer) => (
              <Card key={customer.id} className="card-nassau" data-testid={`customer-${customer.id}`}>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 text-lg mb-3">{customer.name}</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span>
                        {customer.address}
                        {customer.city && `, ${customer.city}`}
                        {customer.county && `, ${customer.county}`}
                        {customer.postcode && ` ${customer.postcode}`}
                        {customer.country && `, ${customer.country}`}
                      </span>
                    </div>
                    {customer.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span>{customer.email}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredCustomers.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No customers found</h3>
              <p className="text-slate-500">Add customers or import from CSV</p>
            </div>
          )}
        </div>
      </main>

      {/* Add Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="name">Customer Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Company or individual name"
                  data-testid="customer-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="customer-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  data-testid="customer-address-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Town/City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Town/City"
                  data-testid="customer-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  value={formData.county}
                  onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                  placeholder="County"
                  data-testid="customer-county-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postal Code</Label>
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="Postal Code"
                  data-testid="customer-postcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                  data-testid="customer-country-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Special instructions or notes"
                  data-testid="customer-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="save-customer-btn"
              >
                {submitting ? "Saving..." : "Save Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-slate-400">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-slate-400">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex flex-col items-center gap-1 text-[#003366]">
            <Users className="h-5 w-5" />
            <span className="text-xs">Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-slate-400">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
